import { Link } from 'react-router-dom';
import { ArrowLeft, Phone, Calendar, Clock, AlertTriangle, CheckCircle, Wrench, Battery, Camera, Monitor, Thermometer, Smartphone, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Layout from '@/components/layout/Layout';
import SEOHead from '@/components/seo/SEOHead';
import { generateArticleSchema, generateBreadcrumbSchema } from '@/components/seo/JsonLd';
import FAQSection from '@/components/FAQSection';
import RelatedPosts from '@/components/RelatedPosts';
import { getRelatedPosts } from '@/data/blogPosts';

const IPhone16CommonProblems = () => {
  const publishDate = '2026-01-13';
  const modifiedDate = '2026-01-13';
  
  const articleSchema = generateArticleSchema({
    title: 'iPhone 16 Common Problems & Solutions: Complete Repair Guide for Patna',
    description: 'Comprehensive guide to iPhone 16 issues including display problems, camera glitches, battery drain, and overheating. Expert solutions from iFix Apple Care Patna.',
    url: 'https://ifixappleservicecenter.in/blog/iphone-16-common-problems-fix-patna',
    image: 'https://ifixappleservicecenter.in/og-image.jpg',
    datePublished: publishDate,
    dateModified: modifiedDate,
  });

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://ifixappleservicecenter.in' },
    { name: 'Blog', url: 'https://ifixappleservicecenter.in/blog' },
    { name: 'iPhone 16 Common Problems', url: 'https://ifixappleservicecenter.in/blog/iphone-16-common-problems-fix-patna' },
  ]);

  const problems = [
    {
      icon: Monitor,
      title: 'Display & Screen Issues',
      issues: [
        'Horizontal lines appearing on screen',
        'Touch response delays or dead zones',
        'Green or pink tint on display',
        'OLED burn-in on static elements',
        'Auto-brightness not working properly'
      ],
      solutions: [
        'Restart your iPhone (hold Side + Volume Down)',
        'Update to latest iOS version',
        'Reset display settings in Accessibility',
        'Disable True Tone temporarily to check',
        'If persistent, screen replacement may be needed'
      ],
      severity: 'medium'
    },
    {
      icon: Camera,
      title: 'Camera & Photography Problems',
      issues: [
        'Camera app freezing or crashing',
        'Blurry photos in low light',
        'Camera Control button not responding',
        'Portrait mode depth issues',
        '48MP ProRAW files corrupting'
      ],
      solutions: [
        'Force close Camera app and reopen',
        'Clean camera lens with microfiber cloth',
        'Reset Camera Control in Settings > Camera',
        'Check for iOS updates with camera fixes',
        'Professional calibration at service center'
      ],
      severity: 'medium'
    },
    {
      icon: Battery,
      title: 'Battery Drain & Charging Issues',
      issues: [
        'Rapid battery drain (20%+ in 2 hours)',
        'Slow charging despite fast charger',
        'Wireless charging not working',
        'Battery percentage stuck or inaccurate',
        'Phone not charging past 80%'
      ],
      solutions: [
        'Disable Background App Refresh',
        'Check Battery Health in Settings',
        'Use Apple-certified chargers only',
        '80% limit is Optimized Charging (normal)',
        'Battery replacement if health below 80%'
      ],
      severity: 'high'
    },
    {
      icon: Thermometer,
      title: 'Overheating Problems',
      issues: [
        'Phone gets hot during normal use',
        'Overheating while charging',
        'Heat warning messages appearing',
        'Performance throttling due to heat',
        'Camera disabled due to temperature'
      ],
      solutions: [
        'Remove case while charging',
        'Avoid using phone in direct sunlight',
        'Close background apps and games',
        'Disable 5G when not needed',
        'Thermal paste replacement for severe cases'
      ],
      severity: 'high'
    },
    {
      icon: Smartphone,
      title: 'Software & Performance Issues',
      issues: [
        'Apps crashing randomly',
        'Slow performance and lag',
        'Face ID not working reliably',
        'Bluetooth connection drops',
        'Wi-Fi disconnecting frequently'
      ],
      solutions: [
        'Force restart (Volume Up, Down, then hold Side)',
        'Reset network settings',
        'Re-register Face ID in Settings',
        'Forget and re-pair Bluetooth devices',
        'Factory reset as last resort (backup first)'
      ],
      severity: 'low'
    }
  ];

  const faqItems = [
    {
      question: 'Is the iPhone 16 display issue covered under warranty?',
      answer: 'Yes, display defects like horizontal lines, green tint, or touch issues are covered under Apple\'s 1-year warranty. If you purchased your iPhone 16 within the last year, visit iFix Apple Care Patna for warranty assessment. We can help determine if your issue qualifies for free repair or replacement.'
    },
    {
      question: 'Why does my iPhone 16 overheat while charging?',
      answer: 'iPhone 16 overheating during charging is often caused by using non-certified chargers, charging in hot environments, or using the phone while charging. The new A18 chip generates more heat under load. Try charging in a cool place, removing the case, and using Apple\'s 20W adapter. If overheating persists, the thermal system may need professional inspection.'
    },
    {
      question: 'How much does iPhone 16 screen repair cost in Patna?',
      answer: 'iPhone 16 screen replacement at iFix Apple Care Patna starts from ₹8,999 for standard display and ₹12,999 for Pro models. We use OEM-quality OLED panels with True Tone calibration. Same-day repair is available for most screen issues. Contact us at +91 97986 53591 for exact pricing based on your model.'
    },
    {
      question: 'Can Camera Control button be repaired separately?',
      answer: 'Yes, the Camera Control button can be repaired independently without replacing the entire frame. This is a common iPhone 16 issue where the capacitive button becomes unresponsive. At iFix Apple Care, we offer Camera Control button repair starting from ₹2,499 with same-day service.'
    },
    {
      question: 'Should I update iOS to fix iPhone 16 problems?',
      answer: 'Yes, Apple regularly releases iOS updates that fix iPhone 16 specific bugs. Always backup before updating. As of January 2026, iOS 18.3 has fixed most display and camera issues. Check Settings > General > Software Update for the latest version. If problems persist after updating, professional diagnosis is recommended.'
    },
    {
      question: 'Is iPhone 16 battery replacement available in Bihar?',
      answer: 'Yes, iFix Apple Care Patna offers iPhone 16 battery replacement with genuine-equivalent cells. iPhone 16 battery replacement costs ₹4,999 with 6-month warranty. The procedure takes about 45 minutes. We recommend replacement when battery health drops below 80% for optimal performance.'
    }
  ];

  const relatedPosts = getRelatedPosts('iphone-16-common-problems-fix-patna', 'iPhone Problems', 3);

  return (
    <Layout>
      <SEOHead
        title="iPhone 16 Common Problems & Solutions | Expert Repair Patna Bihar"
        description="Facing iPhone 16 issues? Complete guide to display problems, camera glitches, battery drain, overheating fixes. Professional iPhone 16 repair at iFix Apple Care Patna."
        url="https://ifixappleservicecenter.in/blog/iphone-16-common-problems-fix-patna"
        type="article"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-b from-secondary to-background">
        <div className="container-apple">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-primary hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <span className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-3 py-1 rounded-full font-medium">
              iPhone Problems
            </span>
            <span>•</span>
            <Clock className="w-4 h-4" />
            <span>18 min read</span>
            <span>•</span>
            <span>Updated: January 13, 2026</span>
          </div>
          
          <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
            iPhone 16 Common Problems & Solutions: Complete Repair Guide for Patna
          </h1>
          
          <p className="text-lg text-muted-foreground max-w-3xl mb-8">
            The iPhone 16 series brought exciting features like Camera Control and the A18 chip, but users across Bihar 
            are experiencing various issues. From display glitches to overheating, this comprehensive guide covers every 
            known iPhone 16 problem with DIY fixes and professional repair options at iFix Apple Care Patna.
          </p>

          {/* Quick CTA */}
          <div className="flex flex-wrap gap-4">
            <Button variant="apple-blue" asChild>
              <a href="tel:9798653591">
                <Phone className="w-4 h-4" />
                Call for iPhone 16 Repair
              </a>
            </Button>
            <Button variant="apple-outline" asChild>
              <a href="https://wa.me/919798653591?text=Hi,%20I%20have%20iPhone%2016%20problem" target="_blank" rel="noopener noreferrer">
                WhatsApp Expert
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-8 bg-secondary/50">
        <div className="container-apple">
          <h2 className="text-xl font-semibold mb-4">Quick Navigation</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {problems.map((problem, index) => (
              <a
                key={index}
                href={`#problem-${index}`}
                className="flex items-center gap-2 p-3 bg-background rounded-xl hover:bg-primary/5 transition-colors text-sm"
              >
                <problem.icon className="w-4 h-4 text-primary" />
                <span className="line-clamp-1">{problem.title.split(' ')[0]}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12">
        <div className="container-apple max-w-4xl">
          <div className="prose prose-lg dark:prose-invert max-w-none">
            <h2 className="text-2xl font-bold mb-4">Understanding iPhone 16 Issues in 2026</h2>
            <p className="text-muted-foreground mb-6">
              Since its launch in September 2024, the iPhone 16 series has been Apple's most advanced smartphone lineup. 
              However, like any new technology, users have reported various problems. At iFix Apple Care Patna, we've 
              repaired hundreds of iPhone 16 devices and identified the most common issues affecting users in Bihar.
            </p>
            
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-6 mb-8">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-amber-900 dark:text-amber-100 mb-2">Before You Start</h3>
                  <p className="text-amber-800 dark:text-amber-200 text-sm">
                    Always backup your iPhone before attempting any fixes. Go to Settings {'>'} [Your Name] {'>'} iCloud {'>'} iCloud Backup. 
                    If your iPhone is under warranty, contact Apple or visit iFix Apple Care for professional diagnosis first.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Sections */}
      {problems.map((problem, index) => (
        <section
          key={index}
          id={`problem-${index}`}
          className={`py-12 ${index % 2 === 0 ? 'bg-secondary/30' : ''}`}
        >
          <div className="container-apple max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                problem.severity === 'high' ? 'bg-red-100 dark:bg-red-900/30' :
                problem.severity === 'medium' ? 'bg-amber-100 dark:bg-amber-900/30' :
                'bg-green-100 dark:bg-green-900/30'
              }`}>
                <problem.icon className={`w-7 h-7 ${
                  problem.severity === 'high' ? 'text-red-600' :
                  problem.severity === 'medium' ? 'text-amber-600' :
                  'text-green-600'
                }`} />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{index + 1}. {problem.title}</h2>
                <span className={`text-sm font-medium ${
                  problem.severity === 'high' ? 'text-red-600' :
                  problem.severity === 'medium' ? 'text-amber-600' :
                  'text-green-600'
                }`}>
                  {problem.severity === 'high' ? 'High Priority' :
                   problem.severity === 'medium' ? 'Medium Priority' : 'Low Priority'}
                </span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Issues */}
              <div className="bg-red-50 dark:bg-red-900/10 rounded-2xl p-6">
                <h3 className="font-semibold text-red-900 dark:text-red-100 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Common Issues
                </h3>
                <ul className="space-y-3">
                  {problem.issues.map((issue, i) => (
                    <li key={i} className="flex items-start gap-3 text-red-800 dark:text-red-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0" />
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Solutions */}
              <div className="bg-green-50 dark:bg-green-900/10 rounded-2xl p-6">
                <h3 className="font-semibold text-green-900 dark:text-green-100 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  DIY Solutions
                </h3>
                <ul className="space-y-3">
                  {problem.solutions.map((solution, i) => (
                    <li key={i} className="flex items-start gap-3 text-green-800 dark:text-green-200">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      {solution}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {problem.severity === 'high' && (
              <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/20">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Need Professional Help?</strong> High priority issues often require 
                  expert diagnosis. Contact iFix Apple Care Patna at{' '}
                  <a href="tel:9798653591" className="text-primary hover:underline">+91 97986 53591</a> for same-day repair.
                </p>
              </div>
            )}
          </div>
        </section>
      ))}

      {/* When to Seek Professional Help */}
      <section className="py-12 bg-foreground text-background">
        <div className="container-apple max-w-4xl">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center">
              <Wrench className="w-7 h-7 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">When to Visit iFix Apple Care Patna</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-background/10 rounded-2xl p-6">
              <h3 className="font-semibold mb-4 text-primary">Visit Immediately If:</h3>
              <ul className="space-y-3 text-background/80">
                <li className="flex items-start gap-3">
                  <ChevronRight className="w-4 h-4 mt-1 text-primary" />
                  Display shows persistent lines or dead pixels
                </li>
                <li className="flex items-start gap-3">
                  <ChevronRight className="w-4 h-4 mt-1 text-primary" />
                  Phone doesn't charge at all
                </li>
                <li className="flex items-start gap-3">
                  <ChevronRight className="w-4 h-4 mt-1 text-primary" />
                  Severe overheating with warning messages
                </li>
                <li className="flex items-start gap-3">
                  <ChevronRight className="w-4 h-4 mt-1 text-primary" />
                  Water or liquid damage occurred
                </li>
                <li className="flex items-start gap-3">
                  <ChevronRight className="w-4 h-4 mt-1 text-primary" />
                  Face ID completely non-functional
                </li>
              </ul>
            </div>

            <div className="bg-background/10 rounded-2xl p-6">
              <h3 className="font-semibold mb-4 text-primary">Our iPhone 16 Services:</h3>
              <ul className="space-y-3 text-background/80">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 mt-1 text-green-400" />
                  Screen replacement from ₹8,999
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 mt-1 text-green-400" />
                  Battery replacement ₹4,999
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 mt-1 text-green-400" />
                  Camera module repair from ₹3,499
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 mt-1 text-green-400" />
                  Charging port repair ₹1,999
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 mt-1 text-green-400" />
                  Free diagnosis for all issues
                </li>
              </ul>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 justify-center">
            <Button variant="apple-blue" size="lg" asChild>
              <a href="tel:9798653591">
                <Phone className="w-5 h-5" />
                Call +91 97986 53591
              </a>
            </Button>
            <Button variant="outline" size="lg" className="bg-transparent border-background/30 text-background hover:bg-background/10" asChild>
              <a href="https://maps.google.com/?q=iFix+Apple+Care+Patna" target="_blank" rel="noopener noreferrer">
                Get Directions to Store
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Prevention Tips */}
      <section className="py-12">
        <div className="container-apple max-w-4xl">
          <h2 className="text-2xl font-bold mb-6">Preventing iPhone 16 Problems</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-secondary rounded-2xl p-6">
              <h3 className="font-semibold mb-3">Protect Your Display</h3>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Use quality tempered glass protector</li>
                <li>• Invest in a good protective case</li>
                <li>• Avoid extreme temperatures</li>
                <li>• Keep away from magnets</li>
              </ul>
            </div>
            
            <div className="bg-secondary rounded-2xl p-6">
              <h3 className="font-semibold mb-3">Battery Care</h3>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Use Apple-certified chargers</li>
                <li>• Enable Optimized Battery Charging</li>
                <li>• Avoid charging to 100% daily</li>
                <li>• Don't use phone while charging</li>
              </ul>
            </div>
            
            <div className="bg-secondary rounded-2xl p-6">
              <h3 className="font-semibold mb-3">Software Maintenance</h3>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Keep iOS updated</li>
                <li>• Restart phone weekly</li>
                <li>• Clear app cache regularly</li>
                <li>• Backup data to iCloud</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 bg-secondary/30">
        <div className="container-apple max-w-4xl">
          <h2 className="text-2xl font-bold mb-8 text-center">Frequently Asked Questions</h2>
          <FAQSection customFaqs={faqItems} showTitle={false} />
        </div>
      </section>

      {/* Related Posts */}
      <section className="py-12">
        <div className="container-apple">
          <RelatedPosts posts={relatedPosts} />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 bg-primary/5">
        <div className="container-apple text-center">
          <h2 className="text-2xl font-bold mb-4">Still Facing iPhone 16 Issues?</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Our expert technicians at iFix Apple Care Patna have specialized training in iPhone 16 repairs. 
            Get free diagnosis and same-day repair for most issues.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button variant="apple-blue" size="lg" asChild>
              <a href="tel:9798653591">
                <Phone className="w-5 h-5" />
                Call Now for Free Diagnosis
              </a>
            </Button>
            <Button variant="apple-outline" size="lg" asChild>
              <Link to="/iphone-service">
                View All iPhone Services
                <ChevronRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default IPhone16CommonProblems;
